/**
 * The client router's data strategy — every client-side load of a route's data
 * is a `/_data/:routeId` fetch to the Hono BFF, carrying the URL being loaded.
 *
 * ★ EVERY LOAD FETCHES. THERE IS NO HYDRATION STASH, and there must not be one
 * again (#7816). Until 2026-10-02 `entry-client.tsx` kept a copy of
 * `window.__staticRouterHydrationData.loaderData` and answered a route's load
 * from it — once per route id, whatever URL was being loaded. That copy could
 * never answer the load it was written for: React Router does not run a
 * route's loader on hydration when the hydration data already holds an entry
 * for it (`getRouteHydrationStatus` in react-router 7.18.2 —
 * `shouldLoad = !hasData && !hasError`), so the only load the stash ever saw
 * was the FIRST LATER one — a search-param navigation, a `revalidate()` — and
 * it answered that load with the SERVER DOCUMENT'S payload, for the URL the
 * document was rendered at.
 *
 * Measured on devstack, 3/3: from `/listings/rent?bedrooms=studio` the filters
 * dialog's Apply navigated to `?bedrooms=3`; both matched routes were
 * "revalidated", ZERO `/_data` requests left the page, and the loader data —
 * the listings AND the `filterSet` the results query is keyed on — was the
 * studio answer, so the pane showed the studio card for as long as anyone
 * watched (20 s). The SECOND Apply on the same page fetched both routes and
 * painted the right answer in 626 ms, because the stash had been spent.
 *
 * A route that genuinely has no hydration data IS loaded on hydration, and
 * that load fetches like any other — there is nothing to stash for it.
 */
import { BFF_ROUTE_URL_HEADER } from '@/shared/lib/bff-headers';
import { decodeDataRouteErrorFrame, isDataRouteErrorStatus } from '@/shared/lib/data-route-error';
import type { DataStrategyMatch, DataStrategyResult } from 'react-router';

/**
 * The slice of React Router's `DataStrategyFunctionArgs` this strategy reads —
 * the URL being loaded, and per match its route id, whether to load it, and
 * the `resolve` that runs the load. React Router's own args are assignable to
 * it, so `createHydratedRouter` takes the function as its `dataStrategy`; the
 * narrower type is what lets a unit test build real, typed arguments.
 */
export interface BffDataStrategyArgs {
  request: Pick<Request, 'url'>;
  matches: readonly (Pick<DataStrategyMatch, 'shouldCallHandler' | 'resolve'> & {
    route: Pick<DataStrategyMatch['route'], 'id'>;
  })[];
}

export async function bffDataStrategy({
  matches,
  request,
}: BffDataStrategyArgs): Promise<Record<string, DataStrategyResult>> {
  // React Router's canonical data-strategy shape: only resolve matches whose
  // handler should run (revalidation/freshness). Matches filtered out keep
  // their existing data rather than being reloaded. `shouldCallHandler` is a
  // METHOD — it must be *called*; the previous `!match.shouldCallHandler`
  // tested the (always-truthy) function reference and never short-circuited.
  const matchesToLoad = matches.filter((match) => match.shouldCallHandler());
  return Object.fromEntries(
    await Promise.all(
      // `as const` makes each entry a `[string, DataStrategyResult]` tuple so
      // `Object.fromEntries` infers `Record<string, DataStrategyResult>`
      // instead of collapsing to `any` (the loose array-overload).
      matchesToLoad.map(
        async (match) =>
          [
            match.route.id,
            await match.resolve(async () => {
              // Only the route URL is sent: the BFF derives the locale from
              // it (the single source). A separate X-Locale header would be a
              // second, divergable locale source — exactly the bug class this
              // closes (ADR 0142).
              const res = await fetch(`/_data/${match.route.id}`, {
                headers: {
                  [BFF_ROUTE_URL_HEADER]: request.url,
                },
              });
              // A refusal is RETHROWN AS A `Response`, never as an `Error`.
              // React Router converts a `Response` thrown inside a data
              // strategy into an `ErrorResponse` carrying its status, which
              // is the value `isRouteErrorResponse` matches and the route's
              // `ErrorBoundary` renders a 404 page from. `new Error(...)` —
              // what this used to throw — is an unrecognised failure, so a
              // deleted or unpublished entity rendered the generic crash
              // boundary on a clicked link while the identical URL typed into
              // the address bar rendered the proper not-found page.
              if (!res.ok) {
                const frame = decodeDataRouteErrorFrame(await res.json().catch(() => undefined));
                // `frame` is absent for any non-2xx this app did not mint (a
                // proxy's HTML error page, an aborted body). The raw status is
                // then the honest floor — still a `Response`, so the failure
                // is still an HTTP error and not a mystery `TypeError`.
                //
                // ⚠ The floor is GUARDED, because the constructor two lines
                // below is not total over it: `new Response('null', {status})`
                // THROWS for a null-body status (304, 101, 103 — all `!ok`, so
                // all reachable here), and that throw lands in the generic
                // crash boundary this whole change exists to remove, from the
                // one code path whose job is to render a clean error page. A
                // status that cannot spell a refusal degrades to 500 through
                // the SAME predicate the frame's own constructor uses.
                const status =
                  frame?.status ?? (isDataRouteErrorStatus(res.status) ? res.status : 500);
                // eslint-disable-next-line @typescript-eslint/only-throw-error -- a `Response` is REQUIRED here, not an Error: React Router converts a thrown Response inside a data strategy into an ErrorResponse carrying its status (the value `isRouteErrorResponse` matches and the route's ErrorBoundary renders a 404 from). Throwing an Error is what produced the generic crash boundary this change removes.
                throw new globalThis.Response(JSON.stringify(frame?.data ?? null), {
                  status,
                  // `frame.statusText` is a legal HTTP reason phrase by
                  // construction (the frame's constructor reduces it to one),
                  // and `res.statusText` came off the wire through fetch's own
                  // parser — so neither can make this constructor throw.
                  statusText: frame?.statusText ?? res.statusText,
                  headers: { 'Content-Type': 'application/json' },
                });
              }
              return res.json();
            }),
          ] as const
      )
    )
  );
}
